import { useContext, useMemo } from 'react'

import { isArray, isFunction } from 'lodash'
import { useSelector } from 'react-redux'

import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import { SchoolContext } from '../../../../Providers/SchoolProvider'
import { firstOrDefault, isObject } from '../../../../utils'
import EntitiesSelector from '../EntitiesSelector'
import CategorizedCascader from '../CategorizedCascader'
import { SchoolModalContext } from '../../../../Providers/Forms/SchoolModal'
import { buildCategoryTree, getCategoryPath } from '../utils/categoryTree'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { callFunctionIfDefined } from '../../../../utils/functionHelper'

const SchoolsSelector = ({
  className = '', empty = false, disabled, grouped = false, schools = null, multiple = 'default', reverse = false, style = {}, values, dropdownRender, placeholder = '', onChange,
  renderOption, onSearch, loading
}) => {
  const ic = useContext(InstitutionContext)
  const sc = useContext(SchoolContext)
  const modalContext = useContext(SchoolModalContext)
  const t = useSelector(selectTranslate)

  const schoolEntities = useMemo(() => {
    let schoolEntities = []

    if (isArray(schools)) {
      schoolEntities = schools
    } else if (isObject(ic)) {
      schoolEntities = ic.schools
    } else if (isObject(sc)) {
      schoolEntities = [sc.school]
    }

    return schoolEntities
  }, [schools, ic, sc])

  const schoolsOptions = useMemo(() => schoolEntities.map(school => {
    const props = {
      value: school.id,
      entity: school
    }

    if (reverse) {
      props.label = school.name
      props.tooltip = school.abbreviation ?? ''
    } else {
      props.label = school.abbreviation ?? school.name
      props.tooltip = school.name
    }

    return props
  }), [schoolEntities, reverse])

  const schoolsTree = useMemo(() => grouped
    ? buildCategoryTree(schoolEntities, { t, getLabel: school => reverse ? school.name : (school.abbreviation ?? school.name) })
    : [], [grouped, schoolEntities, reverse, t])

  const schoolPath = useMemo(() => {
    if (!grouped) {
      return values
    }

    const id = isArray(values) ? firstOrDefault(values) : values

    return getCategoryPath(schoolEntities.find(s => s.id === id))
  }, [grouped, values, schoolEntities])

  /**
   * The cascader only selects a single school, but every consumer of that value (the internship
   * filters, then the InternshipFilters request payload) expects a list of ids : keep the contract.
   */
  const handleCascaderChange = (_value, selectedOptions) => {
    const school = selectedOptions?.[selectedOptions.length - 1]?.data ?? null

    callFunctionIfDefined(onChange, [school === null ? [] : [school.id]])
  }

  if (grouped) {
    return (
      <div className={'flex-col w-100 ' + className}>
        <CategorizedCascader
          options={schoolsTree}
          value={schoolPath}
          disabled={disabled}
          style={style}
          placeholder={placeholder}
          onChange={handleCascaderChange}
        />
      </div>
    )
  }

  return (
    <div className={'flex-col w-100 ' + className}>
      <EntitiesSelector
        selectorClassName='entities-selector-custom'
        disabled={disabled}
        empty={empty}
        entityOptions={schoolsOptions}
        multiple={multiple}
        placeholder={placeholder}
        showSearch
        style={style}
        values={values}
        dropdownRender={modalContext !== undefined && isFunction(dropdownRender) ? dropdownRender(modalContext.setFormHandler) : undefined}
        loading={loading}
        onChange={onChange}
        onSearch={onSearch}
        renderOption={renderOption}
      />
    </div>
  )
}

export default SchoolsSelector
